//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl } from 'utils/UrlUtil';

import { injectEndpoints, tags } from '../api';
import { InstanceFeature } from './types';

// List of BE defined instance features that UI show / hide components
export const InstanceFeatures = {
  INSIGHTS: 'Insights',
  INSIGHTS_NEXTGEN: 'InsightsNextGen',
  INSIGHTS_PROVIDER: 'InsightsProvider',
  PIPELINE_EDITOR_V2: 'PipelineEditorV2',
  DFI_V2: 'DfiV2',
  ABAC: 'ABAC',
  BRAND: 'BRAND',
  ODS: 'ODS',
  BUSINESS_STUDIO: 'BusinessStudio',
  SYNC_AI: 'SyncAI',
  HITL_QUEUES: 'HITLQueues',
  SSO_PROVISIONING: 'SsoProvisioning',
};

const instanceFeatureApi = injectEndpoints({
  endpoints: (builder) => ({
    enableFeature: builder.mutation<InstanceFeature, string>({
      query: (featureName) => ({
        method: 'POST',
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURE_ENABLE, { featureName }),
      }),
      invalidatesTags: (result) => [...(result?.name ? [tags.InstanceFeature(result.name)] : [])],
    }),
    enableInsightsNextGen: builder.mutation<InstanceFeature, void>({
      query: () => ({
        method: 'POST',
        url: makeUrl(DataUrlConstants.ENABLE_INSIGHTS_NEXTGEN),
      }),
      invalidatesTags: (result) => [
        tags.InstanceFeatureList,
        ...(result?.name ? [tags.InstanceFeature(result.name)] : []),
      ],
    }),
    disableFeature: builder.mutation<InstanceFeature, string>({
      query: (featureName) => ({
        method: 'POST',
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURE_DISABLE, { featureName }),
      }),
      invalidatesTags: (result) => [...(result?.name ? [tags.InstanceFeature(result.name)] : [])],
    }),
    getFeatureStatus: builder.query<InstanceFeature, string>({
      query: (featureName) => ({
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURE, { featureName }),
      }),
      providesTags: (result) => [...(result?.name ? [tags.InstanceFeature(result.name)] : [])],
    }),
    getFeatures: builder.query<InstanceFeature[], void>({
      query: () => ({
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURES),
      }),
      providesTags: () => [tags.InstanceFeatureList],
    }),
    requestFeature: builder.mutation<void, string>({
      query: (featureName) => ({
        method: 'POST',
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURE_REQUEST, { featureName }),
      }),
    }),
    getFeatureRequestStatus: builder.query<{ requested: boolean }, string>({
      query: (featureName) => ({
        url: makeUrl(DataUrlConstants.INSTANCE_FEATURE_REQUEST_STATUS, { featureName }),
      }),
    }),
  }),
});

export const {
  useEnableInsightsNextGenMutation,
  useEnableFeatureMutation,
  useDisableFeatureMutation,
  useGetFeatureStatusQuery,
  useGetFeaturesQuery,
  useRequestFeatureMutation,
  useGetFeatureRequestStatusQuery,
} = instanceFeatureApi;
